import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import OutsideClickHandler from 'react-outside-click-handler';
import intlShape from '@/shared/utils/intlShape';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import api from '../../../shared/api';
import config from '../../../config';
import { AddIcon, DeleteIcon } from '../../../shared/components/Icons';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import YesNoModal from '../../../shared/components/YesNoModal';
import OkCancelModal from '../../../shared/components/OkCancelModal';
import ColorSwatchPicker from '../../../shared/components/input/ColorSwatchPicker';
import Checkbox from '../../../shared/components/input/Checkbox';
import { DEFAULT_TAG_COLOR, getTagColorKey } from '../../../shared/components/tag/tagColors';

import { injectStateService, StateService } from '../../../shared/services/StateService';
import { CanCreate, CanDelete } from '../../../shared/components/security';
import authService, { privileges } from '../../../shared/services/AuthService';
import messages from './tagSettingsMessages';

class RelationTagOverview extends Component {
  state = {
    tags: null,
    newTag: null,
    tagToDelete: null,
    openColorPickerId: null,
  };

  componentDidMount() {
    api.base.get(`${config.appUrls.api}/api/relationtag/overview`).then(({ data: tags }) => {
      this.setState({
        tags,
      });
    });
  }

  getColorLabel = key => {
    const { intl } = this.props;
    const colorMessages = {
      grey: messages.ColorGrey,
      blue: messages.ColorBlue,
      yellow: messages.ColorYellow,
      orange: messages.ColorOrange,
      red: messages.ColorRed,
      green: messages.ColorGreen,
    };
    return intl.formatMessage(colorMessages[key] || messages.Color);
  };

  addTag = () => {
    const { newTag, tags } = this.state;
    const { intl } = this.props;

    if (!newTag || !newTag.name) return;

    if (tags.find(x => x.name.toLowerCase() === newTag.name.toLowerCase())) {
      ToastrService.error(intl.formatMessage(messages.TagAlreadyExists));
      return;
    }

    api.base
      .post(`${config.appUrls.api}/api/relationtag`, {
        name: newTag.name,
        color: newTag.color,
        showOnRegistration: newTag.showOnRegistration,
      })
      .then(({ data }) => {
        data.nrOfRelationsInUse = 0;
        if (tags.find(x => x.id === data.id)) {
          // Tag already exists
          this.setState({
            newTag: null,
          });
        } else {
          this.setState(state => ({
            tags: [...state.tags, data],
            newTag: null,
          }));
        }
      });
  };

  editTagName = (tag, value) =>
    api.base
      .put(`${config.appUrls.api}/api/relationtag`, { ...tag, name: value })
      .then(
      ({ data }) => {
        const { name } = data;
        this.setState(state => ({
          tags: state.tags.map(t => {
            if (t.id === tag.id) {
              return { ...t, name };
            }
            return t;
          }),
        }));
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );

  editTagColor = (tag, color) =>
    api.base
      .put(`${config.appUrls.api}/api/relationtag`, { ...tag, color })
      .then(
      ({ data }) => {
        this.setState(state => ({
          tags: state.tags.map(t => {
            if (t.id === tag.id) {
              return { ...t, color: data.color };
            }
            return t;
          }),
          openColorPickerId: null,
        }));
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
        this.setState({ openColorPickerId: null });
      },
    );

  editTagShowOnRegistration = (tag, showOnRegistration) =>
    api.base
      .put(`${config.appUrls.api}/api/relationtag`, { ...tag, showOnRegistration })
      .then(
      ({ data }) => {
        this.setState(state => ({
          tags: state.tags.map(t => {
            if (t.id === tag.id) {
              return { ...t, showOnRegistration: data.showOnRegistration };
            }
            return t;
          }),
        }));
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );

  deleteTag = (tag, force = false) => {
    if (tag.documentCount === 0 || force) {
      return api.base.delete(`${config.appUrls.api}/api/relationtag/${tag.id}`).then(() => {
        this.setState(state => ({
          tags: state.tags.filter(t => t.id !== tag.id),
          tagToDelete: null,
        }));
      });
    }
    this.setState({ tagToDelete: tag });
  };

  render() {
    const { tags, newTag, tagToDelete, openColorPickerId } = this.state;
    const { intl, stateService } = this.props;

    const canUpdate = authService.hasPermission({
      functionality: 'Tags',
      privilege: privileges.Update,
    });

    return (
      <>
        {tags ? (
          <>
            <div className="panel-heading">
              <CanCreate on="Tags">
                <div className="panel-buttons">
                  <button
                    type="button"
                    className="btn btn-inverse"
                    onClick={() =>
                      this.setState({
                        newTag: { name: '', color: DEFAULT_TAG_COLOR, showOnRegistration: false },
                      })
                    }
                  >
                    <i className="fa fa-plus" /> <FormattedMessage {...messages.AddTag} />
                  </button>
                </div>
              </CanCreate>
            </div>
            <div className="panel-body">
              <table className="table table-grid document-tag-grid" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: '9rem' }}>
                      <FormattedMessage {...messages.Color} />
                    </th>
                    <th>
                      <FormattedMessage {...messages.Name} />
                    </th>
                    <th className="text-right" style={{ width: '12rem' }}>
                      <FormattedMessage {...messages.ShowOnRegistration} />
                    </th>
                    <th className="text-right" style={{ width: '16rem' }}>
                      <FormattedMessage {...messages.NrOfRelations} />
                    </th>
                    <th className="text-right" style={{ width: '10rem' }}>
                      <FormattedMessage {...messages.Actions} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map(tag => (
                    <tr key={tag.id}>
                      <td style={{ position: 'relative' }}>
                        <button
                          type="button"
                          className={`color-swatch color-swatch-${getTagColorKey(tag.color)}`}
                          title={this.getColorLabel(getTagColorKey(tag.color))}
                          disabled={!canUpdate}
                          onClick={() =>
                            this.setState({
                              openColorPickerId: openColorPickerId === tag.id ? null : tag.id,
                            })
                          }
                        />
                        {openColorPickerId === tag.id && (
                          <OutsideClickHandler
                            onOutsideClick={() => this.setState({ openColorPickerId: null })}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                zIndex: 10,
                                top: '100%',
                                left: 0,
                                width: 'max-content',
                                background: '#fff',
                                border: '1px solid #ddd',
                                borderRadius: 6,
                                padding: '0.8rem',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              }}
                            >
                              <ColorSwatchPicker
                                value={tag.color}
                                getLabel={this.getColorLabel}
                                onChange={color => this.editTagColor(tag, color)}
                              />
                            </div>
                          </OutsideClickHandler>
                        )}
                      </td>
                      <td>
                        <EditableTextbox
                          value={tag.name}
                          onChange={value => this.editTagName(tag, value)}
                          maxLength="200"
                          readOnly={!canUpdate}
                        />
                      </td>

                      <td className="text-right">
                        <Checkbox
                          onClick={value => this.editTagShowOnRegistration(tag, value)}
                          value={tag.showOnRegistration}
                          disabled={!canUpdate}
                        />
                      </td>

                      <td className="text-right">
                        {tag.nrOfRelationsInUse === 0 ? (
                          0
                        ) : (
                          <a
                            style={{ display: 'block' }}
                            className="bold"
                            href={stateService.getStateUrl('relations.overview', {
                              json: { tags: [tag.name] },
                            })}
                            onClick={() => stateService.gotoRelationOverview({ tags: [tag.name] })}
                          >
                            {tag.nrOfRelationsInUse}
                          </a>
                        )}
                      </td>

                      <td className="text-right">
                        <CanDelete on="Tags">
                          <a onClick={() => this.deleteTag(tag)} className="btn btn-xs btn-icon">
                            <DeleteIcon />
                          </a>
                        </CanDelete>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <OkCancelModal
                show={newTag !== null}
                title={intl.formatMessage(messages.AddTag)}
                onClose={() => this.setState({ newTag: null })}
                onSave={() => this.addTag()}
              >
                {newTag !== null && (
                  <>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={intl.formatMessage(messages.EnterTag)}
                      required
                      value={newTag.name}
                      onChange={e =>
                        this.setState({ newTag: { ...newTag, name: e.target.value } })
                      }
                      onKeyDown={e => {
                        if (e.keyCode === 13) {
                          this.addTag();
                        } else if (e.keyCode === 27) {
                          this.setState({ newTag: null });
                        }
                      }}
                      autoFocus
                    />
                    <div className="margin20T">
                      <label>
                        <FormattedMessage {...messages.Color} />
                      </label>
                      <ColorSwatchPicker
                        value={newTag.color}
                        getLabel={this.getColorLabel}
                        onChange={color => this.setState({ newTag: { ...newTag, color } })}
                      />
                    </div>
                    <div className="margin20T">
                      <Checkbox
                        onClick={showOnRegistration =>
                          this.setState({ newTag: { ...newTag, showOnRegistration } })
                        }
                        value={newTag.showOnRegistration}
                        text={intl.formatMessage(messages.ShowOnRegistration)}
                      />
                    </div>
                  </>
                )}
              </OkCancelModal>

              <YesNoModal
                show={!!tagToDelete}
                title={intl.formatMessage(messages.DeleteTag)}
                message={intl.formatMessage(messages.DeleteRelationTagConfirmationMsg)}
                onNo={() => this.setState({ tagToDelete: null })}
                onSave={() => this.deleteTag(tagToDelete, true)}
              />
            </div>
          </>
        ) : (
          <LoadingAnimation />
        )}
      </>
    );
  }
}

RelationTagOverview.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
};

export default injectStateService(injectIntl(RelationTagOverview));
